/**
 * VAST Service for loading and parsing VAST XML documents
 */

import { VASTClient, VASTParser } from '@dailymotion/vast-client'
import type {
  VASTAdConfig,
  ParsedAd,
  MediaFile,
  TrackingEvent,
  TrackingEventType,
  VASTServiceConfig
} from './types'
import { VASTError, VASTErrorCode } from './types'

/**
 * The URLs of a vast-client URL-template list, each entry an { id, url } object.
 */
function templateUrls(templates: Array<{ url?: string | null }> | undefined): string[] {
  return (templates ?? [])
    .map(template => template?.url?.trim())
    .filter((url): url is string => !!url)
}

export class VASTService {
  private client: VASTClient
  private parser: VASTParser
  private config: VASTServiceConfig
  
  constructor(config: VASTServiceConfig = {}) {
    this.config = {
      timeout: 10000,
      withCredentials: false,
      wrapperLimit: 5,
      ...config
    }
    
    this.client = new VASTClient()
    this.parser = new VASTParser()
  }
  
  /**
   * Load and parse VAST from URL or XML string
   */
  async loadVAST(adConfig: VASTAdConfig): Promise<ParsedAd[]> {
    let vastResponse: any

    if (adConfig.vastUrl) {
      vastResponse = await this.client.get(adConfig.vastUrl, {
        timeout: this.config.timeout,
        withCredentials: this.config.withCredentials,
        wrapperLimit: this.config.wrapperLimit
      })
    } else if (adConfig.vastXml) {
      vastResponse = await this.parser.parseVAST(adConfig.vastXml)
    } else {
      throw new VASTError('No VAST URL or XML provided', VASTErrorCode.TRAFFICKING_ERROR)
    }

    if (!vastResponse || !vastResponse.ads || vastResponse.ads.length === 0) {
      throw new VASTError('No ads found in VAST response', VASTErrorCode.NO_ADS_VAST_RESPONSE)
    }

    return vastResponse.ads.map((ad: any) => this.transformVASTAd(ad, adConfig))
  }
  
  /**
   * Transform VAST ad to our internal format
   */
  private transformVASTAd(vastAd: any, config: VASTAdConfig): ParsedAd {
    // Get the first linear creative (we don't support non-linear ads yet)
    const linear = this.findLinearCreative(vastAd)
    if (!linear) {
      throw new VASTError('No linear creative found', VASTErrorCode.LINEAR_ERROR)
    }
    
    // Extract media files
    const mediaFiles = this.extractMediaFiles(linear)
    if (mediaFiles.length === 0) {
      throw new VASTError('No supported media files found', VASTErrorCode.MEDIA_FILE_NO_SUPPORTED_TYPE)
    }
    
    // Extract tracking events
    const trackingEvents = this.extractTrackingEvents(linear, vastAd)
    
    // Extract skip offset — vast-client parses skipoffset into skipDelay (already in seconds).
    // The publisher's own ceiling is a MINIMUM against it, never a fallback: a creative that
    // declares no skipoffset is unskippable without this, which is the case the setting exists
    // for, and one that declares a longer wait is cut short rather than obeyed.
    const declaredSkipOffset = linear.skipDelay != null && linear.skipDelay >= 0
      ? linear.skipDelay
      : undefined
    const skipOffset = config.forceSkipAfter === undefined
      ? declaredSkipOffset
      : Math.min(declaredSkipOffset ?? Number.POSITIVE_INFINITY, config.forceSkipAfter)

    return {
      id: config.id,
      type: config.type,
      mediaFiles,
      duration: linear.duration || 0,
      skipOffset,
      // vast-client exposes <VideoClicks> as videoClickThroughURLTemplate and
      // videoClickTrackingURLTemplates, each URL an { id, url } object; there is
      // no `videoClicks` field, and reading one left every ad unclickable.
      clickThrough: linear.videoClickThroughURLTemplate?.url || undefined,
      clickTracking: templateUrls(linear.videoClickTrackingURLTemplates),
      trackingEvents,
      // Impressions are { id, url } objects too: passed through as they were,
      // the tracker's macro replacement threw on each and none was ever sent.
      impressionUrls: templateUrls(vastAd.impressionURLTemplates),
      errorUrls: vastAd.errorURLTemplates || [],
      adSystem: vastAd.system?.value,
      adTitle: vastAd.title,
      description: vastAd.description,
      advertiser: vastAd.advertiser,
      pricing: vastAd.pricing,
      survey: vastAd.survey,
      expires: vastAd.expires
    }
  }
  
  /**
   * Find the first linear creative in the ad
   */
  private findLinearCreative(ad: any): any {
    for (const creative of ad.creatives) {
      if (creative.type === 'linear') {
        return creative
      }
    }
    return null
  }
  
  /**
   * Extract and sort media files by quality
   */
  private extractMediaFiles(linear: any): MediaFile[] {
    if (!linear.mediaFiles || linear.mediaFiles.length === 0) {
      return []
    }
    
    // Filter supported media types and transform
    const supportedMimeTypes = [
      'video/mp4',
      'video/webm',
      'video/ogg',
      'application/x-mpegURL',
      'application/vnd.apple.mpegURL'
    ]
    
    const mediaFiles: MediaFile[] = linear.mediaFiles
      .filter((file: any) => supportedMimeTypes.includes(file.mimeType || ''))
      .map((file: any) => ({
        url: file.fileURL || '',
        mimeType: file.mimeType || '',
        width: file.width || 0,
        height: file.height || 0,
        bitrate: file.bitrate,
        scalable: file.scalable,
        maintainAspectRatio: file.maintainAspectRatio,
        codec: file.codec,
        delivery: file.delivery as 'progressive' | 'streaming' | undefined
      }))
      .filter((file: MediaFile) => file.url) // Remove files without URL
    
    // Sort by bitrate (highest first) if available
    mediaFiles.sort((a, b) => {
      if (a.bitrate && b.bitrate) {
        return b.bitrate - a.bitrate
      }
      // Then by resolution
      const aPixels = a.width * a.height
      const bPixels = b.width * b.height
      return bPixels - aPixels
    })
    
    // Apply max bitrate filter if configured
    if (this.config.maxBitrate) {
      return mediaFiles.filter(file => 
        !file.bitrate || file.bitrate <= this.config.maxBitrate!
      )
    }
    
    return mediaFiles
  }
  
  /**
   * Extract tracking events from creative and ad
   */
  private extractTrackingEvents(
    linear: any, 
    ad: any
  ): TrackingEvent[] {
    const events: TrackingEvent[] = []
    const eventMap = new Map<TrackingEventType, string[]>()
    
    // Add creative tracking events
    if (linear.trackingEvents) {
      Object.entries(linear.trackingEvents).forEach(([event, urls]) => {
        const eventType = event as TrackingEventType
        if (!eventMap.has(eventType)) {
          eventMap.set(eventType, [])
        }
        eventMap.get(eventType)!.push(...(urls as string[]))
      })
    }
    
    // Add video clicks tracking
    const clickTracking = templateUrls(linear.videoClickTrackingURLTemplates)
    if (clickTracking.length > 0) {
      if (!eventMap.has('click')) {
        eventMap.set('click', [])
      }
      eventMap.get('click')!.push(...clickTracking)
    }
    
    // Convert map to array
    eventMap.forEach((urls, event) => {
      events.push({ event, urls })
    })
    
    return events
  }
  
  /**
   * Select the best media file based on player constraints
   */
  selectBestMediaFile(
    mediaFiles: MediaFile[], 
    playerWidth: number, 
    playerHeight: number,
    bandwidth?: number
  ): MediaFile | null {
    if (mediaFiles.length === 0) {
      return null
    }
    
    // Filter by player size
    const playerPixels = playerWidth * playerHeight
    const suitableFiles = mediaFiles.filter(file => {
      const filePixels = file.width * file.height
      return filePixels <= playerPixels * 1.5 // Allow up to 150% of player size
    })
    
    const filesToConsider = suitableFiles.length > 0 ? suitableFiles : mediaFiles
    
    // If we have bandwidth info, select based on that
    if (bandwidth && filesToConsider.some(f => f.bitrate)) {
      const withBitrate = filesToConsider.filter(f => f.bitrate)
      const suitable = withBitrate.filter(f => f.bitrate! <= bandwidth * 0.8) // Use 80% of bandwidth
      
      if (suitable.length > 0) {
        return suitable[0] // Already sorted by bitrate
      }
    }
    
    // Otherwise return the first (highest quality that fits)
    return filesToConsider[0]
  }
}